/**
 * APIKEY provider catalog — inference-hosts family (serverless GPU / OSS-model inference hosts).
 * Pure data; merged by apikey/index.ts via spread (god-file decomposition; semantic split).
 */
export const APIKEY_PROVIDERS_INFERENCE = {
  together: {
    id: "together",
    alias: "together",
    name: "Together AI",
    icon: "group_work",
    color: "#0F6FFF",
    textIcon: "TG",
    website: "https://www.together.ai",
    hasFree: false,
    notice: {
      text: "No free tier — Together AI is fully prepaid and requires a minimum $5 credit purchase (valid payment method) before API calls work. The former $25 signup credit was retired.",
      apiKeyUrl: "https://api.together.ai/settings/api-keys",
    },
  },
  // OpenVecta — OpenAI-compatible AI inference gateway (https://openvecta.com/).
  // Catalog seeded from the live /v1/models list (LLMs + embeddings); free credits
  // advertised on signup. Bearer-token auth via Authorization: Bearer ov_sk_…
  openvecta: {
    id: "openvecta",
    alias: "openvecta",
    name: "OpenVecta",
    icon: "vector_polygon",
    color: "#7C3AED",
    textIcon: "OV",
    website: "https://openvecta.com",
    hasFree: true,
    freeNote: "Free credits on signup for OpenAI-compatible inference across LLMs, embeddings, and reasoning models",
  },
  fireworks: {
    id: "fireworks",
    alias: "fireworks",
    name: "Fireworks AI",
    icon: "local_fire_department",
    color: "#7B2EF2",
    textIcon: "FW",
    website: "https://fireworks.ai",
    hasFree: true,
    freeNote: "$1 free starter credits on signup for API testing",
  },
  cerebras: {
    id: "cerebras",
    alias: "cerebras",
    name: "Cerebras",
    icon: "memory",
    color: "#FF4F00",
    textIcon: "CB",
    website: "https://inference.cerebras.ai",
    hasFree: true,
    freeNote: "Free Trial: 1M tokens/day, 30K TPM, 5 RPM — no credit card.",
  },
  nvidia: {
    id: "nvidia",
    alias: "nvidia",
    name: "NVIDIA NIM",
    icon: "developer_board",
    color: "#76B900",
    textIcon: "NV",
    website: "https://build.nvidia.com",
    hasFree: true,
    freeNote: "Free dev access: ~40 RPM, 70+ models (Kimi K2.5, GLM 4.7, DeepSeek V3.2...)",
  },
  nebius: {
    id: "nebius",
    alias: "nebius",
    name: "Nebius AI",
    icon: "cloud",
    color: "#6C5CE7",
    textIcon: "NB",
    website: "https://nebius.com",
    hasFree: true,
    freeNote: "~$1 trial credits on signup for API testing",
  },
  nube: {
    id: "nube",
    alias: "nube",
    name: "Nube.sh",
    icon: "cloud",
    color: "#2563EB",
    textIcon: "NB",
    website: "https://nube.sh",
    hasFree: false,
    notice: {
      text: "OpenAI-compatible gateway (LiteLLM). Bring your own API key — models are resolved live from the account (passthrough).",
      apiKeyUrl: "https://nube.sh/dashboard/api-keys",
    },
  },
  siliconflow: {
    id: "siliconflow",
    alias: "siliconflow",
    name: "SiliconFlow",
    icon: "cloud_queue",
    color: "#5B6EF5",
    textIcon: "SF",
    website: "https://cloud.siliconflow.com",
    hasFree: true,
    freeNote: "$1 free credits plus permanently free models after identity verification",
  },
  hyperbolic: {
    id: "hyperbolic",
    alias: "hyp",
    name: "Hyperbolic",
    icon: "bolt",
    color: "#00D4FF",
    textIcon: "HY",
    website: "https://hyperbolic.xyz",
    hasFree: true,
    freeNote: "$1-5 trial credits on signup for serverless inference",
  },
  "ollama-cloud": {
    id: "ollama-cloud",
    alias: "ollamacloud",
    name: "Ollama Cloud",
    icon: "cloud",
    color: "#58A6FF",
    textIcon: "OC",
    website: "https://ollama.com/settings/keys",
    hasFree: true,
  },
  huggingface: {
    id: "huggingface",
    alias: "hf",
    name: "HuggingFace",
    icon: "face",
    color: "#FFD21E",
    textIcon: "HF",
    website: "https://huggingface.co",
    hasFree: true,
    freeNote: "Free Inference API for thousands of models (Whisper, VITS, SDXL…)",
  },
  "github-models": {
    id: "github-models",
    alias: "ghm",
    name: "GitHub Models",
    icon: "code",
    color: "#238636",
    textIcon: "GH",
    website: "https://github.com/marketplace/models",
    hasFree: true,
    freeNote: "Free GPT-5, o-series, DeepSeek-R1, Llama 4, Grok 3 — GitHub account only.",
    authHint: "Create a GitHub PAT with 'models: read' scope at github.com/settings/tokens",
  },
  deepinfra: {
    id: "deepinfra",
    alias: "deepinfra",
    name: "DeepInfra",
    icon: "hub",
    color: "#2563EB",
    textIcon: "DI",
    website: "https://deepinfra.com",
    hasFree: true,
    freeNote: "Free signup credits for API testing and model exploration",
  },
  "lambda-ai": {
    id: "lambda-ai",
    alias: "lambda",
    name: "Lambda AI",
    icon: "bolt",
    color: "#7C3AED",
    textIcon: "LA",
    website: "https://lambda.ai",
  },
  sambanova: {
    id: "sambanova",
    alias: "samba",
    name: "SambaNova",
    icon: "memory",
    color: "#DC2626",
    textIcon: "SN",
    website: "https://sambanova.ai",
    hasFree: true,
    freeNote: "$5 free credits on signup (30-day validity), no credit card required",
  },
  nscale: {
    id: "nscale",
    alias: "nscale",
    name: "nScale",
    icon: "token",
    color: "#0891B2",
    textIcon: "NS",
    website: "https://nscale.com",
    hasFree: true,
    freeNote: "$5 free credits on signup for inference testing",
  },
  baseten: {
    id: "baseten",
    alias: "baseten",
    name: "Baseten",
    icon: "deployed_code",
    color: "#111827",
    textIcon: "BT",
    website: "https://baseten.co",
    hasFree: true,
    freeNote: "$30 free trial credits for GPU inference",
  },
  publicai: {
    id: "publicai",
    alias: "publicai",
    name: "PublicAI",
    icon: "public",
    color: "#059669",
    textIcon: "PA",
    website: "https://publicai.co",
    // #3558: PublicAI requires an API key (registry authType:"apikey"); signup grants a
    // one-time credit, then it bills per-model. It is NOT a keyless/free tier.
    hasFree: false,
    freeNote: "Requires an API key — one-time signup credit, then paid",
  },
  "featherless-ai": {
    id: "featherless-ai",
    alias: "featherless",
    name: "Featherless AI",
    icon: "flutter_dash",
    color: "#EA580C",
    textIcon: "FL",
    website: "https://featherless.ai",
    hasFree: true,
    freeNote: "Free tier available — no credit card required",
  },
  friendliai: {
    id: "friendliai",
    alias: "friendli",
    name: "FriendliAI",
    icon: "handshake",
    color: "#EC4899",
    textIcon: "FR",
    website: "https://friendli.ai",
    hasFree: true,
    freeNote: "Free tier for serverless inference — no credit card required",
  },
  wandb: {
    id: "wandb",
    alias: "wandb",
    name: "Weights & Biases Inference",
    icon: "monitoring",
    color: "#FFBE0B",
    textIcon: "WB",
    website: "https://wandb.ai",
  },
  "inference-net": {
    id: "inference-net",
    alias: "inet",
    name: "Inference.net",
    icon: "dns",
    color: "#2563EB",
    textIcon: "IN",
    website: "https://inference.net",
    hasFree: true,
    freeNote: "$25 free credits on signup plus research grants available",
  },
  predibase: {
    id: "predibase",
    alias: "predibase",
    name: "Predibase",
    icon: "deployed_code_history",
    color: "#0F766E",
    textIcon: "PB",
    website: "https://predibase.com",
    hasFree: false,
    freeNote: "$25 free trial credits (30-day validity)",
    subscriptionRisk: true,
    riskNoticeVariant: "deprecated",
    deprecated: true,
    deprecationReason:
      "serving.app.predibase.com no longer resolves (sweep 2026-06-19); the managed serving API appears discontinued.",
  },
  bytez: {
    id: "bytez",
    alias: "bytez",
    name: "Bytez",
    icon: "api",
    color: "#6366F1",
    textIcon: "BZ",
    website: "https://bytez.com",
    hasFree: true,
    freeNote: "$1 free credits, refreshes every 4 weeks",
  },
  monsterapi: {
    id: "monsterapi",
    alias: "monster",
    name: "MonsterAPI",
    icon: "cloud",
    color: "#EF4444",
    textIcon: "MA",
    website: "https://monsterapi.ai",
    hasFree: true,
    freeNote:
      "One-time signup trial credits for decentralized GPU inference (no recurring free plan). No credit card required.",
    passthroughModels: true,
    authHint: "Get API key at monsterapi.ai",
  },
  modelscope: {
    id: "modelscope",
    alias: "ms",
    name: "ModelScope",
    icon: "cloud",
    color: "#FF6A00",
    textIcon: "MS",
    website: "https://modelscope.cn",
    hasFree: true,
    // #1764 (upstream 9router): OpenAI-compatible API-Inference. Base URL verified
    // live against ModelScope's own docs — the upstream PR used the `.ai` TLD, but
    // the confirmed production domain is `api-inference.modelscope.cn` (see registry
    // entry + test guard).
    freeNote: "Free tier via ModelScope API-Inference — Alibaba account required.",
  },
  byteplus: {
    id: "byteplus",
    alias: "bpm",
    name: "BytePlus ModelArk",
    icon: "cloud",
    color: "#2563EB",
    textIcon: "BP",
    website: "https://console.byteplus.com/ark",
    hasFree: true,
    notice: {
      text: "Free credits for new accounts. Seed 2.0, Kimi K2 Thinking, GLM 4.7, GPT-OSS-120B available.",
      apiKeyUrl: "https://console.byteplus.com/ark/region:ark+ap-southeast-1/apiKey",
    },
    serviceKinds: ["llm"],
  },
  digitalocean: {
    id: "digitalocean",
    alias: "digitalocean",
    name: "DigitalOcean",
    icon: "cloud",
    color: "#0060FF",
    textIcon: "DO",
    passthroughModels: true,
    website: "https://docs.digitalocean.com/products/ai-platform/",
    hasFree: false,
    notice: {
      text: "Use a DigitalOcean Personal Access Token (dop_v1_...) or a Model Access Key from the Inference console. OAuth tokens (doo_v1_...) may not have the required scopes.",
      apiKeyUrl: "https://cloud.digitalocean.com/account/api/tokens",
    },
    serviceKinds: ["llm"],
  },
};
